import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import sodInstallationImage from '@/assets/sod-installation-service.jpg';
import bulkMaterialsImage from '@/assets/bulk-materials-display.jpg';
import deliveryTruckImage from '@/assets/delivery-truck-service.jpg';
import finishedLandscapeImage from '@/assets/finished-landscape-project.jpg';
import grassVarietiesImage from '@/assets/grass-varieties-texas.jpg';
import decorativeRockImage from '@/assets/decorative-rock-types.jpg';

interface Project {
  id: string;
  title: string;
  titleEs: string;
  location: string;
  category: string;
  categoryEs: string;
  image: string;
  description: string;
  descriptionEs: string;
}

const ProjectGallery = () => {
  const { language } = useLanguage();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const projects: Project[] = [
    {
      id: '1',
      title: 'Premium St. Augustine Grass',
      titleEs: 'Césped St. Augustine Premium',
      location: 'The Woodlands, TX',
      category: 'St. Augustine Grass',
      categoryEs: 'Césped St. Augustine',
      image: sodInstallationImage,
      description: 'High-quality St. Augustine grass perfect for Texas climate. Dense, drought-resistant variety with excellent heat tolerance.',
      descriptionEs: 'Césped St. Augustine de alta calidad perfecto para el clima de Texas. Variedad densa y resistente a la sequía con excelente tolerancia al calor.'
    },
    {
      id: '2',
      title: 'River Rock Collection',
      titleEs: 'Colección de Roca de Río',
      location: 'Cypress, TX',
      category: 'Decorative Rock',
      categoryEs: 'Roca Decorativa',
      image: decorativeRockImage,
      description: 'Premium river rock in various sizes, perfect for drainage and decorative applications. Smooth, naturally weathered stones.',
      descriptionEs: 'Roca de río premium en varios tamaños, perfecta para drenaje y aplicaciones decorativas. Piedras lisas y naturalmente desgastadas.'
    },
    {
      id: '3',
      title: 'Bermuda Grass Varieties',
      titleEs: 'Variedades de Césped Bermuda',
      location: 'Magnolia, TX',
      category: 'Bermuda Grass',
      categoryEs: 'Césped Bermuda',
      image: deliveryTruckImage,
      description: 'Heat-resistant Bermuda grass varieties including Tifway and common Bermuda. Excellent for high-traffic areas.',
      descriptionEs: 'Variedades de césped Bermuda resistentes al calor incluyendo Tifway y Bermuda común. Excelente para áreas de alto tráfico.'
    },
    {
      id: '4',
      title: 'Limestone & Decomposed Granite',
      titleEs: 'Piedra Caliza y Granito Descompuesto',
      location: 'Conroe, TX',
      category: 'Construction Materials',
      categoryEs: 'Materiales de Construcción',
      image: finishedLandscapeImage,
      description: 'Premium limestone and decomposed granite for driveways, pathways, and base material applications.',
      descriptionEs: 'Piedra caliza premium y granito descompuesto para entradas de vehículos, senderos y aplicaciones de material base.'
    },
    {
      id: '5',
      title: 'Zoysia Grass Premium',
      titleEs: 'Césped Zoysia Premium',
      location: 'Spring, TX',
      category: 'Zoysia Grass',
      categoryEs: 'Césped Zoysia',
      image: grassVarietiesImage,
      description: 'Dense, slow-growing Zoysia grass that creates a beautiful, carpet-like lawn with minimal maintenance requirements.',
      descriptionEs: 'Césped Zoysia denso y de crecimiento lento que crea un césped hermoso y similar a una alfombra con requisitos mínimos de mantenimiento.'
    },
    {
      id: '6',
      title: 'Pea Gravel & Bull Rock',
      titleEs: 'Grava de Guisante y Roca Bull',
      location: 'Montgomery County, TX',
      category: 'Gravel & Aggregate',
      categoryEs: 'Grava y Agregados',
      image: bulkMaterialsImage,
      description: 'Quality pea gravel and bull rock in various sizes for drainage, decorative borders, and construction projects.',
      descriptionEs: 'Grava de guisante y roca bull de calidad en varios tamaños para drenaje, bordes decorativos y proyectos de construcción.'
    }
  ];

  const categories = [...new Set(projects.map(p => language === 'en' ? p.category : p.categoryEs))];
  
  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(p => (language === 'en' ? p.category : p.categoryEs) === selectedCategory);

  const openProject = (project: Project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeProject = () => {
    setSelectedProject(null);
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {language === 'en' ? 'Our Recent Projects' : 'Nuestros Proyectos Recientes'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {language === 'en'
              ? 'Discover our premium grass varieties and quality rock materials available across Montgomery County and surrounding areas'
              : 'Descubre nuestras variedades de césped premium y materiales de roca de calidad disponibles en el Condado de Montgomery y áreas circundantes'
            }
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <Badge 
            variant={selectedCategory === 'all' ? "default" : "outline"} 
            className="cursor-pointer px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-colors"
            onClick={() => setSelectedCategory('all')}
          >
            {language === 'en' ? 'All Projects' : 'Todos los Proyectos'}
          </Badge>
          {categories.map((category, index) => (
            <Badge 
              key={index} 
              variant={selectedCategory === category ? "default" : "outline"} 
              className="cursor-pointer px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-colors"
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Badge>
          ))}
        </div>

        {/* Project Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="overflow-hidden hover:shadow-glow transition-all duration-300 cursor-pointer group"
              onClick={() => openProject(project)}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={project.image} 
                  alt={language === 'en' ? project.title : project.titleEs}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  width="455"
                  height="256"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute top-4 right-4">
                  <Badge className="bg-primary">
                    {language === 'en' ? project.category : project.categoryEs}
                  </Badge>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">
                    {language === 'en' ? project.title : project.titleEs}
                  </h3>
                  <div className="flex items-center text-white/90 text-sm">
                    <MapPin className="h-4 w-4 mr-1" />
                    {project.location}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-muted-foreground text-sm line-clamp-3">
                  {language === 'en' ? project.description : project.descriptionEs}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-lg text-muted-foreground mb-6">
            {language === 'en'
              ? 'Ready to transform your property with premium grass and rocks?'
              : '¿Listo para transformar tu propiedad con césped y rocas premium?'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="lg" asChild>
              <a href="/en/quote">
                {language === 'en' ? 'Get Your Free Quote' : 'Obtén tu Cotización Gratis'}
              </a>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href="/en/contact">
                {language === 'en' ? 'Contact Us Today' : 'Contáctanos Hoy'}
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <Dialog open={!!selectedProject} onOpenChange={closeProject}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {language === 'en' ? selectedProject.title : selectedProject.titleEs}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Project Image */}
              <div className="relative h-96 overflow-hidden rounded-lg">
                <img 
                  src={selectedProject.image} 
                  alt={language === 'en' ? selectedProject.title : selectedProject.titleEs}
                  className="w-full h-full object-cover"
                  width="768"
                  height="384"
                  loading="lazy"
                />
              </div>

              {/* Project Details */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {language === 'en' ? 'Project Details' : 'Detalles del Proyecto'}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{selectedProject.location}</span>
                    </div>
                    <div className="flex items-center">
                      <Badge variant="outline" className="text-xs">
                        {language === 'en' ? selectedProject.category : selectedProject.categoryEs}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {language === 'en' ? 'Description' : 'Descripción'}
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    {language === 'en' ? selectedProject.description : selectedProject.descriptionEs}
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default ProjectGallery;